import { useEffect, useState } from 'react';
import { brandedKey } from './brandedKey.js';

const KEY = brandedKey('trailerConsent');

/**
 * Whether the viewer has agreed to load trailers from YouTube.
 *
 * Embedding the player — and even its thumbnail — contacts Google and hands
 * over the viewer's IP address, so nothing may be requested from YouTube until
 * somebody has actively asked for it. Until then the card shows its generated
 * cover and the page stays free of third-party requests.
 *
 * The choice is remembered per browser and can be taken back.
 */
const listeners = new Set<(granted: boolean) => void>();

function read(): boolean {
  try {
    return window.localStorage.getItem(KEY) === 'granted';
  } catch {
    // Private mode or blocked storage: treat as "not agreed".
    return false;
  }
}

function write(granted: boolean): void {
  try {
    if (granted) window.localStorage.setItem(KEY, 'granted');
    else window.localStorage.removeItem(KEY);
  } catch {
    // The in-memory state below still carries the choice for this page view.
  }
  for (const listener of listeners) listener(granted);
}

export interface TrailerConsent {
  granted: boolean;
  grant: () => void;
  revoke: () => void;
}

export function useTrailerConsent(): TrailerConsent {
  const [granted, setGranted] = useState(read);

  useEffect(() => {
    listeners.add(setGranted);
    return () => {
      listeners.delete(setGranted);
    };
  }, []);

  return {
    granted,
    grant: () => write(true),
    revoke: () => write(false),
  };
}
